import React, { useMemo, useState, useEffect } from 'react';
import { useTable } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Badge, Nav, Modal, Form, Placeholder, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faUser, 
  faDollarSign, 
  faCalendarAlt,
  faHandHoldingHeart,
  faEnvelope,
  faPhone,
  faSignOutAlt,
  faExclamationTriangle,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import './DonorProfile.css';
import { AuthService, DonationService, SubscriptionService } from '../../Services/Axios/Api';
import { getToken, getDonorEmail } from '../../Services/Axios/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Subscriptions from './Subscriptions';

const DonorProfile = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('donations');
    const [subscriptions, setSubscriptions] = useState([]);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [newPhone, setNewPhone] = useState('');
    const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
    const [donorData, setDonorData] = useState({
        name: 'John Doe',
        email: '',
        phone: '+1 (234) 567-890',
        totalDonations: 0,
        totalAmount: 0,
        currency: 'EGP',
        joinDate: '2023-01-15',
        lastDonation: '',
        donations: []
    });

    useEffect(() => {
        const fetchDonorData = async () => {
            try {
                const token = getToken();
                if (token === 'undefined') {
                    navigate('/membership');
                    return;
                }

                const email = getDonorEmail();
                const response = await DonationService.getDonorStatistics(email);
                const subscriptionsResponse = await SubscriptionService.getDonorSubscriptions(email);
                
                if (response.isSuccess) {
                    const data = response.result;
                    setDonorData({
                        ...donorData,
                        name: localStorage.getItem('Name') || email,
                        email: email,
                        totalDonations: data.totalDonations,
                        totalAmount: data.completedDonationsAmount,
                        lastDonation: data.lastDonationDate,
                        phone: data.phoneNumber,
                        currency: data.donationHistory[0]?.currency || 'EGP',
                        donations: data.donationHistory.map(donation => ({
                            id: donation.donationId,
                            campaignName: donation.campaignName,
                            amount: donation.amount,
                            currency: donation.currency,
                            date: donation.donationDate,
                            status: donation.paymentStatus,
                            transactionId: donation.transactionId
                        }))
                    });

                    if (!data.phoneNumber && !sessionStorage.getItem('phoneSkipped')) {
                        setShowPhoneModal(true);
                    }
                }
                if(subscriptionsResponse.isSuccess){
                    setSubscriptions(subscriptionsResponse.result);
                }
            } catch (error) {
                console.error('Error fetching donor data:', error);
                toast.error(t('DonorProfile.ErrorFetchingData', 'Failed to load profile data'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchDonorData();
    }, [navigate]);

    const columns = useMemo(
        () => [
            {
                Header: t('DonorProfile.Table.Campaign'),
                accessor: 'campaignName',
                Cell: ({ value }) => <span className="campaign-name">{value}</span>,
            },
            {
                Header: t('DonorProfile.Table.Amount'),
                accessor: 'amount',
                Cell: ({ row }) => (
                    <Badge bg="success" className="donation-amount">
                        {row.original.currency} {row.original.amount.toLocaleString()}
                    </Badge>
                ),
            },
            {
                Header: t('DonorProfile.Table.Date'),
                accessor: 'date',
                Cell: ({ value }) => {
                    const date = new Date(value);
                    const formattedDate = new Intl.DateTimeFormat(i18n.language || 'en', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    }).format(date);
                    return (
                        <div className="donation-date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {formattedDate}
                        </div>
                    );
                },
            },
            {
                Header: t('DonorProfile.Table.Status'),
                accessor: 'status',
                Cell: ({ value }) => (
                    <Badge bg={value === 'Completed' ? 'success' : 'warning'} className="status-badge">
                        {value !== "LinkGenerated" ? value : t('DonorProfile.Table.LinkGenerated')}
                    </Badge>
                ),
            },
            {
                Header: t('DonorProfile.Table.TransactionId'),
                accessor: 'transactionId',
                Cell: ({ value }) => (
                    <span className="transaction-id">{value}</span>
                ),
            },
        ],
        [t, i18n.language]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data: donorData.donations });

    const handleLogout = async () => {
        try {
            await AuthService.Logout();
        } catch (error) {
            console.error('Logout failed:', error);
        }
    }

    const handleUpdate = async () => {
        setIsLoading(true);
        try {
            const email = getDonorEmail();
            const response = await DonationService.getDonorStatistics(email);
            const subscriptionsResponse = await SubscriptionService.getDonorSubscriptions(email);
            if (response.isSuccess) {
                const data = response.result;
                setDonorData(prev => ({
                    ...prev,
                    donations: data.donationHistory.map(donation => ({
                        id: donation.donationId,
                        campaignName: donation.campaignName,
                        amount: donation.amount,
                        currency: donation.currency,
                        date: donation.donationDate,
                        status: donation.paymentStatus,
                        transactionId: donation.transactionId
                    }))
                }));
            }
            if(subscriptionsResponse.isSuccess){
                setSubscriptions(subscriptionsResponse.result);
            }
        } catch (error) {
            console.error('Error updating donor data:', error);
            toast.error(t('DonorProfile.ErrorFetchingData', 'Failed to refresh data'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePhone = async () => {
        if (!newPhone) return;
        setIsUpdatingPhone(true);
        try {
            await AuthService.updateProfile({ phoneNumber: newPhone });
            setDonorData(prev => ({ ...prev, phone: newPhone }));
            setShowPhoneModal(false);
            toast.success(t('DonorProfile.PhoneUpdated', 'Phone number saved successfully!'));
        } catch (error) {
            toast.error(error.message || 'Failed to save phone number');
        } finally {
            setIsUpdatingPhone(false);
        }
    };

    const handleSkipPhone = () => {
        sessionStorage.setItem('phoneSkipped', 'true');
        setShowPhoneModal(false);
    };

    if (isLoading) {
        return (
            <Container className="donor-profile-container mt-5 mb-5" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                <Placeholder as="h1" animation="wave"><Placeholder xs={4} /></Placeholder>
                <Row className="g-4 mb-4 mt-2">
                    <Col lg={4}>
                        <Card className="donor-card"><Card.Body><Placeholder as={Card.Title} animation="wave"><Placeholder xs={6} /></Placeholder></Card.Body></Card>
                    </Col>
                    <Col lg={8}>
                        <Row className="g-4">
                            <Col md={6}><Card className="donor-card"><Card.Body><Placeholder as={Card.Text} animation="wave"><Placeholder xs={8} /> <Placeholder xs={7} /></Placeholder></Card.Body></Card></Col>
                            <Col md={6}><Card className="donor-card"><Card.Body><Placeholder as={Card.Text} animation="wave"><Placeholder xs={8} /> <Placeholder xs={7} /></Placeholder></Card.Body></Card></Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container className="donor-profile-container donor-profile-page mt-5 mb-5" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <div className="donor-profile-header">
                <Button 
                    variant="link" 
                    className="back-button"
                    onClick={() => navigate('/campaigns')}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    {t('DonorProfile.Back')}
                </Button>
                <h1 className="donor-profile-title">{t('DonorProfile.Title')}</h1>
            </div>

            {!donorData.phone && (
                <Alert variant="warning" className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {t('DonorProfile.CompleteProfilePrompt', 'Please complete your profile by providing a phone number.')}
                    </div>
                    <Button variant="outline-warning" size="sm" onClick={() => setShowPhoneModal(true)}>
                        {t('DonorProfile.UpdateNow', 'Update Now')}
                    </Button>
                </Alert>
            )}

            <Row className="g-4 mb-4">
                <Col lg={4}>
                    <Card className="donor-card profile-card">
                        <Card.Body className="text-center">
                            <div className="donor-avatar">
                                <FontAwesomeIcon icon={faUser} />
                            </div>
                            <h5 className="donor-name mt-3">{donorData.name}</h5>
                            <div className="donor-meta">
                                <Button 
                                    variant="link" 
                                    className="logout-button mt-3 text-decoration-none"
                                    onClick={()=>handleLogout()}
                                >
                                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> {t('DonorProfile.Logout')}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Row className="g-4">
                        <Col md={6}>
                            <Card className="donor-card info-card">
                                <Card.Body>
                                    <h5 className="card-section-title">
                                        <FontAwesomeIcon icon={faUser} className="me-2" />
                                        {t('DonorProfile.ContactInformation')}
                                    </h5>
                                    <ul className="info-list">
                                        <li>
                                            <FontAwesomeIcon icon={faEnvelope} />
                                            <span>{donorData.email}</span>
                                        </li>
                                        <li>
                                            <FontAwesomeIcon icon={faPhone} />
                                            <span>{donorData.phone?donorData.phone:'N/A'}</span>
                                        </li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="donor-card stats-card">
                                <Card.Body>
                                    <h5 className="card-section-title">
                                        <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                                        {t('DonorProfile.DonationStats')}
                                    </h5>
                                    <div className="stats-grid">
                                        <div className="stat-item">
                                            <div className="stat-value">{donorData.totalDonations}</div>
                                            <div className="stat-label">{t('DonorProfile.TotalDonations')}</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-value">{donorData.currency} {donorData.totalAmount.toLocaleString()}</div>
                                            <div className="stat-label">{t('DonorProfile.TotalGiven')}</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-value">
                                                {donorData.lastDonation ? (() => {
                                                    const date = new Date(donorData.lastDonation);
                                                    if (isNaN(date.getTime())) return t('DonorProfile.N/A', 'N/A');
                                                    return new Intl.DateTimeFormat(i18n.language || 'en', {
                                                        day: '2-digit', month: '2-digit', year: 'numeric'
                                                    }).format(date);
                                                })() : t('DonorProfile.N/A', 'N/A')}
                                            </div>
                                            <div className="stat-label">{t('DonorProfile.LastDonation')}</div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Nav variant="tabs" className="mb-4">
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'donations'} 
                        onClick={() => setActiveTab('donations')}
                    >
                        <FontAwesomeIcon icon={faHandHoldingHeart} className="me-2" />
                        {t('DonorProfile.Donations')}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'subscriptions'} 
                        onClick={() => setActiveTab('subscriptions')}
                    >
                        <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                        {t('DonorProfile.Subscriptions')}
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'donations' ? (
                <Card className="donor-card donations-card">
                    <Card.Body>
                        <div className="card-header-section d-flex justify-content-between align-items-center flex-wrap">
                            <h5 className="card-section-title mb-0 mb-2">
                                <FontAwesomeIcon icon={faHandHoldingHeart} className="me-2" />
                                {t('DonorProfile.DonationHistory')}
                            </h5>
                            <div className="total-donations">
                                {t('DonorProfile.ShowingDonations', { count: donorData.donations.length })}
                            </div>
                        </div>
                        
                        {donorData.donations.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning mb-3 fs-1" />
                                <h4>{t('DonorProfile.NoDonations')}</h4>
                                <Button className="mt-3" variant="primary" onClick={() => navigate('/campaigns')}>
                                    {t('DonorProfile.DonateNow', 'Donate Now')}
                                </Button>
                            </div>
                        ) : (
                            <div className="table-responsive">
                            <table className="donations-table" {...getTableProps()}>
                                <thead>
                                    {headerGroups.map(headerGroup => {
                                        const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                                        return (
                                            <tr key={key} {...restHeaderGroupProps}>
                                                {headerGroup.headers.map(column => {
                                                    const { key: columnKey, ...restHeaderProps } = column.getHeaderProps();
                                                    return (
                                                        <th 
                                                            key={columnKey}
                                                            {...restHeaderProps}
                                                        >
                                                            {column.render('Header')}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                    {rows.map(row => {
                                        prepareRow(row);
                                        const { key, ...restRowProps } = row.getRowProps();
                                        return (
                                            <tr key={key} {...restRowProps}>
                                                {row.cells.map(cell => {
                                                    const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                                    return (
                                                        <td key={cellKey} {...restCellProps}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                <Subscriptions 
                    subscriptions={subscriptions} 
                    onUpdate={handleUpdate}
                />
            )}

            <Modal show={showPhoneModal} onHide={handleSkipPhone} backdrop="static" keyboard={false} centered dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                <Modal.Header>
                    <Modal.Title>{t('DonorProfile.CompleteYourProfile', 'Complete Your Profile')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('DonorProfile.PhoneMissingPrompt', 'Please provide a phone number so we can better assist you and keep you updated on your impact.')}</p>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Membership.PhoneNumber', 'Phone Number')}</Form.Label>
                        <Form.Control 
                            type="tel" 
                            placeholder={t('Membership.EnterPhoneNumber', 'Enter your phone number')} 
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleSkipPhone} disabled={isUpdatingPhone}>
                        {t('DonorProfile.Skip', 'Skip for now')}
                    </Button>
                    <Button variant="primary" onClick={handleSavePhone} disabled={!newPhone || isUpdatingPhone}>
                        {isUpdatingPhone ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                        {t('DonorProfile.Save', 'Save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DonorProfile;